// ui/src/hooks/useFramework.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { FrameworkConfig, SaveFrameworkRequest } from '../types';

// Query keys
export const frameworkKeys = {
  all: ['framework'] as const,
  public: () => [...frameworkKeys.all, 'public'] as const,
  active: () => [...frameworkKeys.all, 'active'] as const,
  versions: () => [...frameworkKeys.all, 'versions'] as const,
  version: (v: string) => [...frameworkKeys.all, 'version', v] as const,
  templates: () => [...frameworkKeys.all, 'templates'] as const,
  template: (name: string) => [...frameworkKeys.all, 'template', name] as const,
  redFlags: () => [...frameworkKeys.all, 'redFlags'] as const,
};

// Public framework
export function usePublicFramework() {
  return useQuery({
    queryKey: frameworkKeys.public(),
    queryFn: () => api.getPublicFramework(),
  });
}

// Admin: Active framework
export function useActiveFramework() {
  return useQuery({
    queryKey: frameworkKeys.active(),
    queryFn: () => api.getActiveFramework(),
  });
}

// Admin: List versions
export function useFrameworkVersions() {
  return useQuery({
    queryKey: frameworkKeys.versions(),
    queryFn: () => api.listFrameworkVersions(),
  });
}

// Admin: Get specific version
export function useFrameworkVersion(version: string) {
  return useQuery({
    queryKey: frameworkKeys.version(version),
    queryFn: () => api.getFrameworkVersion(version),
    enabled: !!version,
  });
}

// Admin: Save framework
export function useSaveFramework() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SaveFrameworkRequest) => api.saveFramework(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: frameworkKeys.all });
    },
  });
}

// Admin: Validate framework
export function useValidateFramework() {
  return useMutation({
    mutationFn: (config: FrameworkConfig) => api.validateFramework(config),
  });
}

// Admin: List templates
export function useFrameworkTemplates() {
  return useQuery({
    queryKey: frameworkKeys.templates(),
    queryFn: () => api.listFrameworkTemplates(),
  });
}

// Admin: Get template
export function useFrameworkTemplate(name: string) {
  return useQuery({
    queryKey: frameworkKeys.template(name),
    queryFn: () => api.getFrameworkTemplate(name),
    enabled: !!name,
  });
}

// Admin: Save template
export function useSaveFrameworkTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, config }: { name: string; config: FrameworkConfig }) =>
      api.saveFrameworkTemplate(name, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: frameworkKeys.templates() });
    },
  });
}

// Admin: Delete template
export function useDeleteFrameworkTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => api.deleteFrameworkTemplate(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: frameworkKeys.templates() });
    },
  });
}

// Admin: Get predefined red flags
export function useRedFlags() {
  return useQuery({
    queryKey: frameworkKeys.redFlags(),
    queryFn: () => api.getRedFlags(),
  });
}
